import { z } from 'zod';

// Imported from the file, not the `access` barrel: both domains are re-exported
// from the package root, and going through the barrel would make them circular
// at module-init time (the same reason `permission-catalog.ts` reaches into
// `../integrations/integration-catalog`).
import { type CatalogPermissionKey } from '../../access/permissions/permission-catalog/permission-catalog';

import { MEDIA_KINDS, type MediaKind } from '../media-kind/media-kind';
import { MemberReadSchema, type MemberRead } from '../member-read/member-read';

/**
 * The raster types the finalize pipeline can actually decode.
 *
 * Deliberately narrow and deliberately **not** operator-configurable: an
 * allowlist entry is a claim that the server can decode the bytes, extract
 * dimensions, compute a blur placeholder and (later) embed a face from them.
 * Allowing `image/tiff` or `image/heic` would not widen what members may
 * upload — it would produce assets that finalize, then break face-match and
 * blur downstream. The **size** ceiling is a different question and is a
 * setting (`media.maxUploadBytes`), because "how large" is policy while "which
 * codec" is a capability of this code.
 */
export const RASTER_IMAGE_MIMES = ['image/jpeg', 'image/png', 'image/webp'] as const;

/**
 * The document types a kind may declare **instead of** being decoded (F1).
 *
 * A document is the second mime family, not a fourth raster: finalize verifies
 * its header and stores it with no dimensions and no blur placeholder, the
 * face-match records "not an image" rather than running, and every viewer
 * branches on the family before it touches the bytes. A kind opts into this
 * family on purpose — see `kindAcceptsDocuments` — because opting in also
 * means accepting a file no decoder ever looked inside.
 */
export const DOCUMENT_MIMES = ['application/pdf'] as const;

export type RasterImageMime = (typeof RASTER_IMAGE_MIMES)[number];
export type DocumentMime = (typeof DOCUMENT_MIMES)[number];

const PAPERWORK_MIMES = [...RASTER_IMAGE_MIMES, ...DOCUMENT_MIMES] as const;

// Spellings real clients emit for a type already on the list. iOS's image
// picker sniffs the first byte and reports every JPEG as `image/jpg`.
const MIME_ALIASES: Readonly<Record<string, string>> = { 'image/jpg': 'image/jpeg' };

/**
 * Everything the platform knows about one kind of media *before* any value is
 * stored — the registry N12's authorization rules are read from.
 *
 * It lives in contracts rather than in the api because all three consumers need
 * it: the api authorizes reads with it, the mobile app pre-validates a capture
 * against `mimeTypes` and decides whether to offer "replace" (singleton) or "add
 * another", and the staff portal shows the "this view is recorded" notice derived
 * from `auditReads`.
 */
export const MediaKindPolicySchema = z
  .object({
    kind: z.enum(MEDIA_KINDS),
    /** Lower-case, parameter-free MIME types. */
    mimeTypes: z.array(z.string().regex(/^[a-z]+\/[a-z0-9.+-]+$/)).min(1),
    /**
     * Who the asset belongs to.
     *
     * `member` — one member owns it, holds a quota of it, and is the only
     * non-staff caller who may read it. `platform` — it belongs to the
     * deployment: the row carries **no owner**, only the staff member who
     * uploaded it, and it is created through the admin surface rather than by
     * the member routes. It is declared rather than derived because every guard
     * that hangs off it (which quota is counted, which upload path may create
     * it, whether `isOwner` can ever be true) must be a compile-time fact.
     */
    ownership: z.enum(['member', 'platform']),
    /**
     * The catalog permission a **non-owner** must hold to be minted a read URL.
     * `null` would mean owner-only with no staff path at all — no kind is that
     * today, but the shape says a kind may be.
     */
    staffPermission: z.string().min(1).nullable(),
    /**
     * The catalog permission the admin media surface requires to upload or
     * delete this kind — and `null` for a member kind, which has no admin
     * upload path at all: a member's evidence is uploaded by the member.
     */
    managePermission: z.string().min(1).nullable(),
    /** Every non-owner read is written to the audit trail before the URL is returned (N12). */
    auditReads: z.boolean(),
    /**
     * A non-owner read of this kind is additionally subject to
     * `verification.genderMatchedReview`: when that setting is on, the caller's
     * gender must match the owner's.
     *
     * **Declared, never derived.** The temptation is to infer it from
     * `staffPermission === 'verification.review'` — and that is exactly the
     * string coincidence `verification-step-key.ts` refuses to rely on for the
     * same pair of registries. A kind's authorization rules are facts about the
     * kind; a second kind placed behind the same permission for an unrelated
     * reason must not silently inherit a gender rule.
     *
     * Only meaningful for a `member`-owned kind: a `platform` asset has no owner,
     * so there is no gender to match and the flag is always false there.
     */
    isGenderMatchedStaffRead: z.boolean(),
    /**
     * Whether — and how — a **non-owner member** may be minted a read url for
     * this kind: `none`, through the owner's photo-privacy policy
     * (`owner_policy`, P17a), or on authentication alone (`open`). The three
     * values and the reasoning behind each are in `member-read.ts`.
     *
     * **Declared, never derived** — the same argument as
     * `isGenderMatchedStaffRead` immediately above, and the more dangerous half
     * of it. The tempting inference is `ownership === 'member'`, which is true of
     * `id_document` and `selfie` as well: taking it would put a member's identity
     * evidence behind the photo ladder the moment anybody wrote that line, and
     * the ladder *has* an allowing branch. A kind reaches a member because
     * somebody wrote the mode down, never by resembling one that does.
     *
     * The registry's spec pins each mode's membership exactly — `owner_policy` is
     * `profile_photo` alone, `open` is `venue_photo` alone — along with the
     * ownership each mode requires and the two combinations that must never occur
     * for **any** non-`none` mode: audited reads (an audit row per avatar render
     * buries the reads that matter) and gender-matched staff review (a rule about
     * identity evidence).
     *
     * *(Was `isMemberReadable: boolean` until P25a2. A venue photograph is
     * platform-owned and member-readable at once, which the boolean could not
     * say: its one allowing branch passed `ownerUserId` to a ladder that refuses
     * `null`.)*
     */
    memberRead: MemberReadSchema,
    /**
     * A member holds at most **one**, and a second upload is **refused** — they
     * delete the existing one first.
     *
     * Refusing rather than replacing is deliberate: replace-on-upload would
     * destroy a verified ID document the moment a new capture was merely *started*,
     * so an abandoned retake would leave the member with nothing while their
     * verification case still needed evidence. The client uses this flag to show
     * "replace" affordances — delete, then capture — not to assume the server will
     * overwrite.
     */
    singleton: z.boolean(),
    /**
     * Whether the OWNER may delete an asset of this kind through `DELETE
     * /media/:id` (F3).
     *
     * `true` for content a member curates — a photo, a document they are
     * re-taking. `false` where the asset is a **record** the platform relies on
     * as much as the member does: a payment receipt bound to an order is the
     * proof of a payment, and P23b4's argument for keeping staff-attached
     * receipts out of member hands applies to a receipt the member sent
     * themselves — they may read it, they may not destroy it. Always `false`
     * for a platform kind, which has no owner to ask.
     */
    ownerDeletable: z.boolean(),
  })
  .strict();

/**
 * The descriptor as code sees it. `staffPermission` is narrower here than in the
 * schema — a **declared** catalog key rather than any string — so a typo is a
 * compile error rather than a permission that silently resolves to deny (N22).
 * The schema stays the runtime shape check the registry's own spec applies.
 */
export interface MediaKindPolicy {
  kind: MediaKind;
  mimeTypes: readonly string[];
  ownership: MediaOwnership;
  staffPermission: CatalogPermissionKey | null;
  managePermission: CatalogPermissionKey | null;
  auditReads: boolean;
  isGenderMatchedStaffRead: boolean;
  memberRead: MemberRead;
  singleton: boolean;
  ownerDeletable: boolean;
}

/** Who a kind's assets belong to — see `MediaKindPolicySchema.ownership`. */
export type MediaOwnership = 'member' | 'platform';

/**
 * The **kind registry** — the only place a media kind's constraints come into
 * existence. Extending the platform with a new kind is one entry here plus one
 * `MEDIA_KINDS` member; there is no second list to keep in step.
 */
const POLICIES: readonly MediaKindPolicy[] = [
  {
    // Verification evidence. Any country's ID or passport — it is a private
    // file reviewed by human eyes, never OCR'd, so nothing here is
    // country-specific (U16). One per member: a second upload is refused until the
    // first is deleted. A civil ID is often issued as a PDF, so this kind takes
    // the document family too: the header is verified, nothing is decoded, and
    // the face-match records that there was no photograph to compare (F1).
    kind: 'id_document',
    mimeTypes: PAPERWORK_MIMES,
    ownership: 'member',
    staffPermission: 'verification.review',
    managePermission: null,
    auditReads: true,
    // Gender-matched review is one of the platform's stated differentiators, and
    // a queue filter is not an access control: a reviewer who obtains an asset id
    // by any other route reaches the bytes directly. So the rule is enforced at
    // the MINT as well, on this flag.
    isGenderMatchedStaffRead: true,
    // Identity evidence. No member-to-member path exists to it at all — not a
    // blurred one, not a matched one, and not an open one.
    memberRead: 'none',
    singleton: true,
    // A retake: the member deletes the one they hold and captures again — the
    // singleton refusal above is what makes that the path.
    ownerDeletable: true,
  },
  {
    kind: 'selfie',
    mimeTypes: RASTER_IMAGE_MIMES,
    ownership: 'member',
    staffPermission: 'verification.review',
    managePermission: null,
    auditReads: true,
    isGenderMatchedStaffRead: true,
    memberRead: 'none',
    singleton: true,
    ownerDeletable: true,
  },
  {
    // Member-facing content. Staff read it to moderate it, which is routine
    // rather than sensitive, so reads are not audited — and a member keeps
    // several, up to the `media.maxProfilePhotos` setting (enforced at presign as
    // a friendly refusal, and again inside the promoting transaction, which is the
    // check that cannot be raced).
    kind: 'profile_photo',
    mimeTypes: RASTER_IMAGE_MIMES,
    ownership: 'member',
    staffPermission: 'media.review',
    managePermission: null,
    auditReads: false,
    isGenderMatchedStaffRead: false,
    // **The one kind another member reaches through its OWNER'S policy** (P17a)
    // — the ladder at the mint: both accounts eligible, no block in either
    // direction, and either the owner's `photoVisibility` is `visible` or an
    // active match exists. The payload's `photosBlurred` is a hint; this is what
    // decides. Not `open`: a member's photograph is exactly the disclosure this
    // platform is built to control.
    memberRead: 'owner_policy',
    singleton: false,
    ownerDeletable: true,
  },
  {
    // The deployment's own imagery — a logo, and whatever else a `media`-typed
    // setting comes to point at (U16: brand facts are settings, never code).
    //
    // **Platform-owned**: the row has no member owner, so it survives the
    // deletion of the staff account that uploaded it, and `isOwner` is false for
    // every caller — the permission decides, always. Reads are not audited
    // because a logo is the opposite of private evidence: it is shown to every
    // operator who opens the brand settings, and an audit row per render would
    // bury the reads that matter. The manage key is `brand.*` rather than
    // `media.*` on purpose — see the comment on the catalog keys.
    //
    // Raster only: `image/svg+xml` is a script-bearing document rather than an
    // image, and the finalize decoder would have to either refuse it or stop
    // being a decoder. Not a document kind either — a logo is drawn, not read.
    kind: 'brand_asset',
    mimeTypes: RASTER_IMAGE_MIMES,
    ownership: 'platform',
    staffPermission: 'brand.read',
    managePermission: 'brand.manage',
    auditReads: false,
    // No owner, so there is no gender to match against — see the field's doc.
    isGenderMatchedStaffRead: false,
    // `none`, not `open` — and the distinction is the whole point of the axis.
    // "Platform-owned" does not imply "shown to members": a logo reaches an
    // operator through `brand.read`, and reaches the phone through ONE public
    // route, `GET /media/brand-logo` (D10), which resolves the asset the
    // `brand.logo*MediaId` settings name and mints it — never through the
    // per-id member mint, which would let anyone holding an id read any brand
    // asset staff ever uploaded. Only a kind that members are *meant* to browse
    // by id opts into `open`.
    memberRead: 'none',
    singleton: false,
    ownerDeletable: false,
  },
  {
    // The receipt behind a direct payment — a bank transfer slip or a cash
    // office stub the member sent to support, attached to one
    // `billing.direct_payment_orders` row by staff.
    //
    // **Platform-owned, and the argument is sharper than the logo's.** The
    // receipt is the deployment's *financial record*: it outlives the order's
    // decision, and the member whose payment is in dispute must not be able to
    // read or destroy the proof of it. Member ownership would make `isOwner`
    // true for exactly that person and would put the file on the
    // account-deletion path (`deleteAllForOwner`), which is the failure this
    // declaration exists to prevent. The row therefore carries no owner, only
    // the staff member who uploaded it.
    kind: 'payment_evidence',
    // **Rasters and PDFs (F1).** Bank apps in this market issue PDF receipts
    // routinely, and until F1 staff had to photograph them. The two reasons the
    // kind refused the type were both answered by code, not by policy: finalize
    // has a document branch (the `%PDF-` header is verified; nothing is decoded,
    // no dimensions or blur are stored), and the bytes are served from the
    // STORAGE origin by a presigned GET into a cross-origin frame — they never
    // execute inside the portal's own origin, which an earlier comment here
    // wrongly assumed. `image/svg+xml` stays off: an image format that carries
    // script is a different thing from a document format handled as a document.
    // The admin control names these types from this list rather than repeating
    // them.
    mimeTypes: PAPERWORK_MIMES,
    ownership: 'platform',
    // **The higher of billing's two keys, on both halves.** P23b3a put the
    // billing-event *list* behind `billing.read` and its vendor *payload* behind
    // `billing.manage`, audited; this is that split applied to a document. A
    // receipt carries the member's own bank details, so an operator holding only
    // `billing.read` sees that one is on file — the order is documented — and is
    // told which permission opens it, rather than having the mint fired and
    // refused on their behalf.
    staffPermission: 'billing.manage',
    managePermission: 'billing.manage',
    // Every read is recorded (N12). This is the first PLATFORM-owned kind to
    // audit its reads, so it is the first to reach `recordView` with a null
    // owner — see the note there.
    auditReads: true,
    // No owner, so there is no gender to match against — see the field's doc.
    isGenderMatchedStaffRead: false,
    // A member has no path to it at all, least of all the person whose order it
    // documents.
    memberRead: 'none',
    // **Not a singleton, and the distinction is worth stating.** `singleton`
    // caps what one OWNER may hold, and the owner here is the deployment — a
    // platform that has taken ten thousand payments holds ten thousand receipts.
    // "One receipt per order" is a different invariant, and it is enforced where
    // it belongs: on `direct_payment_orders.evidence_media_id`, whose attach
    // refuses when the column is already set.
    singleton: false,
    ownerDeletable: false,
  },
  {
    // A photograph of a partner institution's meeting place (P25a2) — what a
    // matched pair look at before agreeing to be introduced there.
    //
    // **Platform-owned**, like the logo and the receipt: a venue belongs to the
    // deployment's directory, not to any member, so the row carries no owner and
    // survives the staff account that uploaded it. Staff keys are the module's
    // own — `meetings.read` to see the directory, `meetings.manage` to change it
    // — rather than `brand.*`, which would let whoever maintains the logo edit
    // the partner estate.
    kind: 'venue_photo',
    mimeTypes: RASTER_IMAGE_MIMES,
    ownership: 'platform',
    staffPermission: 'meetings.read',
    managePermission: 'meetings.manage',
    // A venue photograph is the opposite of private evidence: it is drawn for
    // every member who opens the directory, and an audit row per tile would bury
    // the reads that matter (N12).
    auditReads: false,
    // No owner, so there is no gender to match against — see the field's doc.
    isGenderMatchedStaffRead: false,
    // **The kind this axis was widened for.** Platform-owned *and* member-read:
    // there is no owner to hold a photo-privacy policy, and no policy to consult,
    // so being an authenticated member is the whole check. `open` is not
    // "public" — nothing here is readable without a session, and staff still
    // come through `meetings.read` like every other staff read.
    memberRead: 'open',
    // A venue has several photographs, and the cap is **per venue** rather than
    // per owner — the owner here is the deployment, whose whole library is
    // unbounded. `meetings.maxVenuePhotos` is enforced as the attach claim on
    // `meetings.venue_photos`, the `payment_evidence` shape exactly.
    singleton: false,
    ownerDeletable: false,
  },
  {
    // The receipt a MEMBER uploads for a direct payment they opened from the
    // phone (F3), bound to one `billing.direct_payment_orders` row by the
    // member's own submit.
    //
    // **Member-owned, and the argument is the ordinary upload path.** The member
    // presigns and finalizes it through `POST /media/presign-upload` like an
    // `id_document`; a platform kind's presign is gated by its manage permission,
    // which a member never holds, so a member-uploaded platform asset would mean
    // a route that bypasses the registry. What `payment_evidence` protects by
    // ownership — the person whose payment is in dispute must not destroy the
    // proof — this kind protects on its own axis: `ownerDeletable: false`.
    kind: 'payment_receipt',
    // The bank's exported slip ONLY — a screenshot can be of anything (owner
    // decision, 2026-09-13). The staff-attached receipt keeps the wider family.
    mimeTypes: DOCUMENT_MIMES,
    ownership: 'member',
    // The higher of billing's two keys, audited — `payment_evidence`'s reasoning
    // verbatim: a receipt carries the member's bank details, so the support
    // desk sees THAT one is on file and the manage key is what opens it.
    staffPermission: 'billing.manage',
    managePermission: null,
    auditReads: true,
    // Not identity evidence: a receipt is reviewed by whoever holds the billing
    // key, and gender-matched review is a rule about the verification pair.
    isGenderMatchedStaffRead: false,
    // No other member has any path to it — not blurred, not matched, not open.
    memberRead: 'none',
    // A member may hold several: one per order they ever opened, and a rejected
    // order keeps its receipt as the record of the decision. The per-product
    // "one open order" rule lives on the order, as a partial unique index, not
    // here as a count.
    singleton: false,
    ownerDeletable: false,
  },
];

export const MEDIA_KIND_POLICIES: readonly MediaKindPolicy[] = POLICIES;

const BY_KIND: ReadonlyMap<MediaKind, MediaKindPolicy> = new Map(
  POLICIES.map((policy) => [policy.kind, policy]),
);

/**
 * The kind's policy. Total rather than partial: `MediaKind` is a closed set and
 * a missing entry is a construction error caught by this module's own spec, so
 * a caller never has to handle "a kind with no rules".
 */
export function getMediaKindPolicy(kind: MediaKind): MediaKindPolicy {
  const policy = BY_KIND.get(kind);

  if (policy === undefined) {
    throw new Error(`No media policy is declared for the kind "${kind}".`);
  }

  return policy;
}

/**
 * Whether this kind may be uploaded as this MIME type.
 *
 * Parameters (`; charset=binary`) are stripped and the type is lower-cased,
 * because a client's content-type header legitimately carries both — but an
 * empty or blank type is a **refusal**, not a wildcard.
 */
export function isAllowedMediaMime(kind: MediaKind, mime: string): boolean {
  const normalized = normalizeMime(mime);

  return normalized !== null && getMediaKindPolicy(kind).mimeTypes.includes(normalized);
}

/**
 * The bare `type/subtype`, lower-cased, with a known alias folded onto the
 * declared spelling — or `null` when there is nothing to compare. The one place
 * a MIME string is normalized (U3): the presign check, the finalize head-check
 * and every client picker have to agree on what "the same type" means, which is
 * how `image/jpg` from the iOS picker once refused every library pick on one
 * platform while the other two passed it.
 */
export function normalizeMime(mime: string): string | null {
  const [base] = mime.split(';');
  const normalized = (base ?? '').trim().toLowerCase();

  if (normalized === '') return null;

  return MIME_ALIASES[normalized] ?? normalized;
}

/** Whether the type, once normalized, is one the finalize pipeline decodes. */
export function isRasterImageMime(mime: string): boolean {
  const normalized = normalizeMime(mime);

  return normalized !== null && (RASTER_IMAGE_MIMES as readonly string[]).includes(normalized);
}

/** Whether the type, once normalized, is a document — verified by header, never decoded. */
export function isDocumentMime(mime: string): boolean {
  const normalized = normalizeMime(mime);

  return normalized !== null && (DOCUMENT_MIMES as readonly string[]).includes(normalized);
}

/**
 * Whether a kind takes the document family at all — what decides whether a
 * picker offers "choose a file" beside the camera, read off the registry so no
 * client keeps its own list of which kinds are paperwork.
 */
export function kindAcceptsDocuments(kind: MediaKind): boolean {
  return getMediaKindPolicy(kind).mimeTypes.some((mime) => isDocumentMime(mime));
}
